import React, { useState, useEffect } from 'react';
import { ChevronDown, Download, FileSignature } from '@insurancemenu/imenu-components-lib/dist/icons';
import { DropdownOptionType } from '@insurancemenu/imenu-components-lib/dist/types';
import { downloadByUrl } from '@insurancemenu/imenu-components-lib/dist/helpers/downloadByUrl';
import {
  RequestSignatureContext,
  RequestSignatureModalStore,
} from 'store/modules/upload-documents/ui-stores/requestSignatureModalStore';
import { autorun } from 'mobx';
import { useEnrollmentPacketPageStore } from 'store/pages/enrollment-packet/pageStore';
import { RequestSignatureModal } from 'components/common/documents-upload/RequestSignatureModal';
import { observer } from 'mobx-react-lite';
import { Dropdown } from '@insurancemenu/imenu-components-lib';
import { File } from 'types/policyService';
import { buildPdfLink } from 'global/services-helpers/pdfService';
import { DropdownButtonStyled, DropdownOpenedButtonStyled, DropdownOptionTextStyled } from './styles';

interface DropdownProps {
  file: File;
}

const openAndDownloadFile = (name: string, link: string): void => {
  if (name.toLowerCase().endsWith('.pdf')) {
    window.open(link, '_blank');
  } else {
    downloadByUrl(link);
  }
};

function useRequestSignatureModalStore({
  periodId,
  documentId,
  refreshData,
  withFile,
}: RequestSignatureContext): RequestSignatureModalStore {
  const [store, setStore] = useState(
    () => new RequestSignatureModalStore({ periodId, documentId, refreshData, withFile }),
  );

  useEffect(() => {
    const disposer = autorun(() => {
      setStore(new RequestSignatureModalStore({ periodId, documentId, refreshData, withFile }));
    });

    return (): void => disposer();
  }, [documentId, refreshData]);
  return store;
}

export const MoreDropdown: React.FC<DropdownProps> = observer(({ file }) => {
  const { buildPdfsList, setLoading, uncompleteDocumentsStep, enrollmentPeriodId, isESignatureAvailable } =
    useEnrollmentPacketPageStore();
  const requestSignatureModalStore = useRequestSignatureModalStore({
    periodId: enrollmentPeriodId,
    documentId: file.id,
    refreshData: buildPdfsList,
    withFile: file,
  });

  async function handleRequestSignature(): Promise<void> {
    setLoading(true);
    // We agreed to uncomplete step even on the modal open, to make the flow easier.
    await uncompleteDocumentsStep();
    setLoading(false);

    requestSignatureModalStore.modalDelegate.open();
  }

  function getDropdownOptions(): DropdownOptionType[] {
    const dropdownOptions: DropdownOptionType[] = [
      {
        value: 'downloadFile',
        onOptionClick: () => openAndDownloadFile(file.name, buildPdfLink(file.id)),
        label: (
          <DropdownOptionTextStyled>
            <Download width="2.4rem" />
            Download File
          </DropdownOptionTextStyled>
        ),
      },
    ];

    if (file.signatureRequestDate === null && isESignatureAvailable) {
      dropdownOptions.push({
        value: 'requestSignature',
        onOptionClick: handleRequestSignature,
        label: (
          <DropdownOptionTextStyled>
            <FileSignature width="2.4rem" />
            Request Signature
          </DropdownOptionTextStyled>
        ),
      });
    }
    return dropdownOptions;
  }

  return (
    <>
      <Dropdown
        name="More"
        options={getDropdownOptions()}
        closedElement={
          <DropdownButtonStyled color="primary" type="text" rightIconProp={<ChevronDown />}>
            More
          </DropdownButtonStyled>
        }
        openedElement={
          <DropdownOpenedButtonStyled color="primary" rightIconProp={<ChevronDown />}>
            More
          </DropdownOpenedButtonStyled>
        }
      />
      <RequestSignatureModal modalStore={requestSignatureModalStore} />
    </>
  );
});
